import { type } from 'arktype';

// ============================================================================
// Permission-id list — shared narrow predicate
// ============================================================================

/**
 * Optional, nullable permission-id list with the following semantics:
 * - omitted     → keep current restriction state (used by PATCH)
 * - null        → clear all direct assignments and return to inherit-all mode
 * - non-empty[] → full replacement restricted set
 * - []          → REJECTED (empty restricted mode is meaningless)
 *
 * Each id must be a valid permission slug (1–255 chars, matching `rbac.permissions.id`).
 * Duplicates inside the array are deduplicated server-side before persistence — they
 * are NOT rejected at validation time.
 */
const permissionIdsSchema = type('(string > 0 & string <= 255)[] | null').narrow(
  (v, ctx) => v === null || v.length > 0 || ctx.mustBe('a non-empty list of permission ids or null')
);

// ============================================================================
// Request schemas
// ============================================================================

export const createApiKeySchema = type({
  name: type('string > 0 & string <= 100').configure({
    description: 'Human-readable label for the API key. 1-100 characters.',
    examples: ['CI/CD pipeline', 'Payroll integration'],
  }),
  'expiresIn?': type('number > 0 | null').configure({
    description:
      'Lifetime of the key in seconds. Null (or omitted) creates a non-expiring key; otherwise must be a positive number. Optional and nullable.',
    examples: [2592000, null],
  }),
  'permissionIds?': permissionIdsSchema.configure({
    description:
      'Optional direct permission allowlist. Omit or pass `null` to create an inherit-all key. Pass a non-empty array of permission ids to restrict the key. Empty arrays are rejected.',
    examples: [null, ['employees:read', 'calendars:read']],
  }),
}).configure({ description: 'Payload to create a new API key. Full key plaintext is only returned once on creation.' });

export type CreateApiKey = typeof createApiKeySchema.infer;

export const updateApiKeySchema = type({
  'name?': type('string > 0 & string <= 100').configure({
    description: 'Updated label for the API key. 1-100 characters. Optional.',
    examples: ['Payroll integration (prod)'],
  }),
  'enabled?': type('boolean').configure({
    description: 'Updated enabled flag. Set false to revoke without deleting the key record. Optional.',
    examples: [true, false],
  }),
  'permissionIds?': permissionIdsSchema.configure({
    description:
      'Update the direct permission allowlist. Omit to keep the current state, `null` to clear and inherit-all, or a non-empty array to full-replace. Empty arrays are rejected.',
    examples: [null, ['employees:read']],
  }),
}).configure({ description: 'Partial update payload for an existing API key (rename, toggle enabled, or change permission scope).' });

export type UpdateApiKey = typeof updateApiKeySchema.infer;

// ============================================================================
// Response interfaces
// ============================================================================

/**
 * A single API key as returned in list endpoints.
 * The `start` field is the safe-to-display prefix (first ~8 chars).
 * The full key is never returned after creation.
 */
export interface ApiKeyListItem {
  id: string;
  name: string | null;
  start: string | null;
  enabled: boolean;
  expiresAt: Date | null;
  createdAt: Date;
  /** Owner of the key — included in the admin system-wide list. */
  owner: {
    id: string;
    name: string | null;
    email: string;
  };
  /**
   * True when the key has no direct permission allowlist rows and therefore
   * inherits its owner's full effective permission set at runtime.
   * Populated by the list endpoints once Unit B ships; on rows produced by
   * pre-Unit-B code paths this defaults to true (inherit-all is the legacy behavior).
   */
  inheritsAllPermissions: boolean;
  /**
   * Number of direct permission allowlist rows attached to the key. Zero when
   * `inheritsAllPermissions` is true. Populated by the list endpoints once
   * Unit B ships.
   */
  restrictedPermissionCount: number;
}

/**
 * A single permission entry that the current user is allowed to assign to one
 * of their own API keys. Returned by the assignable-permissions endpoint and
 * embedded inside the key detail view.
 */
export interface ApiKeyPermissionItem {
  id: string;
  description: string | null;
}

/**
 * Full edit view for a single API key owned by the current user.
 * Includes the restriction state plus the list of direct permission ids
 * currently attached so the edit form can pre-select them.
 */
export interface ApiKeyDetailsItem {
  id: string;
  name: string | null;
  start: string | null;
  enabled: boolean;
  expiresAt: Date | null;
  createdAt: Date;
  inheritsAllPermissions: boolean;
  permissionIds: string[];
  restrictedPermissionCount: number;
}

/**
 * The create response — includes the plaintext `key` field.
 * This is the only time the full key is available; it is not stored in plaintext.
 */
export interface ApiKeyCreatedItem {
  id: string;
  name: string | null;
  start: string | null;
  key: string;
  createdAt: Date;
}

export const apiKeyCreatedSchema = type({
  id: 'string',
  name: 'string | null',
  start: 'string | null',
  key: type('string').configure({
    description: 'Plaintext API key. Returned exactly once; store it now.',
    examples: ['sk_live_9fE2q7Z6aK1b3X8pT0uVwYrLcDhJmS5n'],
  }),
  createdAt: 'string',
}).configure({ description: 'Newly created API key, including its one-time plaintext `key`.' });
